import axios from "axios";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import BootcampCard from "@/components/BootcampCard";
import { motion } from "framer-motion";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import webengage from "@/webengage";
import { NextPageContext } from "next";
import { cn } from "@/lib/utils";
import Breadcrumb from "@/components/BreadCrumb";

export async function getServerSideProps(ctx: NextPageContext) {
  try {
    const subCategory = ctx.query.category as string;
    const spiritualityId = "clcj0zqjl000ogr0qnev4m235";

    const { data: fetchedSubCategories } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}categories/sub-categories/${spiritualityId}`,
      {
        headers: { "Accept-Encoding": "gzip,deflate,compress" },
      }
    );

    let fetchUrl = `${process.env.NEXT_PUBLIC_BASE_API}bootcamps?getFutureBootcamps=true&select=sessions&category=Spirituality`;

    if (subCategory && typeof subCategory === "string") {
      const sanitizedSubCategory = subCategory.replace(/%/g, "&");
      const subCategoryId = fetchedSubCategories.data.find(
        (tag: any) => tag.slug === sanitizedSubCategory
      )?.id;
      if (subCategoryId) {
        fetchUrl = `${process.env.NEXT_PUBLIC_BASE_API}bootcamps/subCategory/${subCategoryId}?getFutureBootcamps=true`;
      }
    }

    const { data: bootcamps } = await axios.get(fetchUrl, {
      headers: { "Accept-Encoding": "gzip,deflate,compress" },
    });

    return {
      props: { bootcamps: bootcamps?.data, tags: fetchedSubCategories?.data },
    };
  } catch (err: any) {
    console.error(`Failed to fetch bootcamps: ${err.message}`);
    return {
      props: {
        bootcamps: [],
        tags: [],
      },
    };
  }
}

type Tag = { [key: string]: { name: string; id: string; slug: string } };

export default function Bootcamps({
  bootcamps,
  tags,
}: {
  bootcamps: any;
  tags: any;
}) {
  const router = useRouter();

  const [activeCategory, setActiveCategory] = useState<{
    name: string;
    id: string;
    slug: string;
  } | null>(null);

  const tagsArray = tags || [];
  const TAGS: Tag = {};
  tagsArray.forEach((tag: any) => {
    TAGS[tag.slug.replace(/&/g, "%")] = {
      name: tag?.name,
      id: tag?.id,
      slug: tag?.slug.replace(/&/g, "%"),
    };
  });

  useEffect(() => {
    setActiveCategory(
      router.query.category ? TAGS[router.query.category.toString()] : null
    );
  }, [router.query.category]);

  useEffect(() => {
    const _webengage = webengage();
    if (_webengage)
      _webengage.track("Bootcamps page viewed", {
        source: "Astrolearn",
      });
  }, []);

  return (
    <Layout phone="+918048822397">
      <Head>
        <title>Astrology & Occult Paid Courses | AstroLearn</title>
        <meta
          name="description"
          content="Access premium astrology and occult courses online. Learn live from experts, explore predictive sciences, and get certified to advance your career!"
        />
      </Head>

      <Breadcrumb classNames="font-lato ml-4 mt-4" />
      <div className="flex flex-col gap-4 font-lato px-4 mt-1">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ease: "linear", duration: 0.25 }}
          className="flex flex-col gap-4 md:flex-row md:justify-between"
        >
          <div className="flex gap-4 justify-start items-center">
            <ArrowLeftIcon
              className="w-4 h-4 hover:cursor-pointer md:hidden"
              onClick={() => router.back()}
            />
            <h3 className="text-[#1D2026] max-lg:text-xl font-bold">
              Explore Bootcamps
            </h3>
          </div>
          <div className="max-md:w-full md:max-w-[900px] flex gap-2 sm:gap-4  justify-center md:justify-end">
            <button
              onClick={() => router.push("/bootcamps")}
              className={cn(
                "rounded-full whitespace-nowrap text-sm md:text-[15px] px-3 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none",
                !activeCategory ? "bg-[#FFD700]" : "bg-[#9A702633]"
              )}
            >
              All
            </button>
            {tagsArray.map((tag: any) => (
              <button
                onClick={() => {
                  const _webengage = webengage();
                  if (_webengage) {
                    _webengage.track("Category Clicked", {
                      industry: tag.name,
                      source: "Astrolearn",
                    });
                  }

                  const sanitizedSlug = tag.slug.replace(/&/g, "%");
                  router.push(`/bootcamps?category=${sanitizedSlug}`);
                }}
                className={cn(
                  "rounded-full text-sm md:text-[15px] px-3 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none whitespace-nowrap",
                  tag?.name === activeCategory?.name
                    ? "bg-[#FFD700]"
                    : "bg-[#9A702633]"
                )}
                key={tag?.name}
              >
                {tag?.name}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="flex flex-col py-2 gap-8 md:grid md:grid-cols-2 lg:grid-cols-3 justify-center items-center">
          {bootcamps?.map((bootcamp: any, index: number) => (
            <BootcampCard
              initial={{ opacity: 0, translateY: 40 }}
              animate={{ opacity: 1, translateY: 0 }}
              transition={{ duration: 0.4, delay: 0.2 * index, type: "tween" }}
              viewport={{ once: true }}
              bootcamp={bootcamp}
              key={bootcamp?.id}
            />
          ))}
        </div>
      </div>
    </Layout>
  );
}
